import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { setupCache, buildMemoryStorage, defaultKeyGenerator, defaultHeaderInterpreter } from 'axios-cache-interceptor';
import { API_ROUTES } from '../enums';
import {
  getPreviewAccessToken,
  getRefreshFromTokenLocalStorage,
  getTokenFromLocalStorage,
  setRefreshTokenLocalStorage,
  setTokenLocalStorage,
} from '../helpers/localStorage';
import { store } from '../store/store';
import { normalizeAxiosError } from './normalizeAxiosError';

export const SESSION_EXPIRED_SNACKBAR_KEY = 'sessionExpiredSnackbar';

export function markSessionExpiredForSnackbar() {
  try {
    localStorage.setItem(SESSION_EXPIRED_SNACKBAR_KEY, '1');
  } catch {
    // ignore
  }
}

export type AxiosError = {
  response: {
    status: number;
    data: {
      message: string;
    };
  };
  config: any;
};

const cacheDebounce = true;

// Public URLs...
const PUBLIC_URLS = {
  // Exact matches
  exact: new Set(['auth', 'auth/login', 'auth/logout', 'contact', API_ROUTES.REFRESH_TOKEN]),

  // Prefix matches
  prefixes: [API_ROUTES.REGISTER, API_ROUTES.FORGOT_PASSWORD, API_ROUTES.RESTORE_PASSWORD],
};

/**
 * Check if a URL is public (doesn't require authentication)
 */
function isPublicURL(url: string): boolean {
  if (PUBLIC_URLS.exact.has(url)) {
    return true;
  }
  // Check prefix matches
  return PUBLIC_URLS.prefixes.some((prefix) => url.startsWith(prefix));
}

// Token refresh state management
let isRefreshingToken = false;
let refreshSubscribers: ((token: string) => void)[] = [];

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers when token refresh completes
 */
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

/**
 * Refresh the access token using the refresh token
 * Uses raw Axios to bypass the concurrency queue and prevent deadlocks
 */
async function refreshAccessToken(): Promise<string> {
  try {
    const refreshToken = getRefreshFromTokenLocalStorage();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // This prevents deadlock when maxConcurrent requests all hit 401
    const rawAxios = Axios.create({
      baseURL: process.env.REACT_APP_API_URL,
    });

    //const response = await rawAxios.post(API_ROUTES.REFRESH_TOKEN, { headers: { Authorization: `Bearer ${refreshToken}` } });
    const response = await rawAxios.get(API_ROUTES.REFRESH_TOKEN, { headers: { Authorization: `Bearer ${refreshToken}` } });

    const { accessToken, refreshToken: newRefreshToken } = response.data;

    // Update tokens in localStorage
    setTokenLocalStorage(accessToken);
    if (newRefreshToken) {
      setRefreshTokenLocalStorage(newRefreshToken);
    }

    return accessToken;
  } catch (error: any) {
    // If refresh fails with 401, user needs to log in again
    if (error?.response?.status === 401) {
      // Clear tokens and reload to login
      setTokenLocalStorage('');
      setRefreshTokenLocalStorage('');
      markSessionExpiredForSnackbar();
      window.location.reload();
    }
    throw error;
  }
}

export const cancelTokenSource = Axios.CancelToken.source();

let axiosInstance: AxiosInstance;
console.log('************** INIT axios.ts ************************');

// -------------- WITH CACHE -----------------
if (cacheDebounce) {
  // Create an axios instance with caching
  const axiosBase = Axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    // timeout: 5000,
  });

  axiosInstance = setupCache(axiosBase, {
    ttl: 1000 * 1, // Started at 5 seconds, changed to 1  - just enough to dedupe API calls on page loading...  e.g. //ttl: 1000 * 60 * 5, // 5 minutes
    location: 'client',
    methods: ['get', 'head'], // Do not cache POST requests (but can), but if refreshing on a POST do allow that to retry...  Default:   ['get', 'head'],
    storage: buildMemoryStorage(), // Keep short-lived cache out of localStorage so uploads keep resume/progress space
    generateKey: defaultKeyGenerator, //  Override this if we need more selective keys...  Probably do..
    headerInterpreter: defaultHeaderInterpreter,
    cachePredicate: {
      // All cacheable status codes defined in RFC 7231
      statusCheck: (status) => [200, 203, 300, 301, 302, 404, 405, 410, 414, 501].includes(status),
    },
    // //Read [Debugging](https://axios-cache-interceptor.js.org/guide/debugging) page
    debug: console.log,
  });
  console.log('************** cache configured');
} else {
  // -------------- NO CACHE -----------------
  console.log('************** NO CACHE');

  axiosInstance = Axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    cancelToken: cancelTokenSource.token,
  });
}

type QueueItem = {
  config: InternalAxiosRequestConfig;
  resolve: (value: InternalAxiosRequestConfig) => void;
  reject: (reason?: any) => void;
};

export class AxiosRequestManager {
  private axiosInstance: AxiosInstance;
  private maxConcurrent: number;
  private activeCount = 0;
  private queue: QueueItem[] = [];
  private paused = false;

  constructor(axiosInstance: AxiosInstance, maxConcurrent = 6) {
    this.axiosInstance = axiosInstance;
    this.maxConcurrent = maxConcurrent;
    this.setupInterceptors();
  }

  pause() {
    this.paused = true;
  }

  unpause() {
    this.paused = false;
    this.processQueue();
  }

  private enqueue(task: QueueItem) {
    this.queue.push(task);
    this.processQueue();
  }

  private processQueue() {
    if (this.paused) return;
    if (this.activeCount >= this.maxConcurrent) return;
    if (this.queue.length === 0) return;

    const task = this.queue.shift()!;
    this.activeCount++;

    // Allow the request to proceed
    task.resolve(task.config);
  }

  private onRequest(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    let accessToken = getTokenFromLocalStorage();
    const refreshToken = getRefreshFromTokenLocalStorage();

    // Public URLs do not require auth
    if (isPublicURL(config.url || '')) {
      return new Promise((resolve, reject) => {
        this.enqueue({ config, resolve, reject });
      });
    }

    if (!accessToken && !refreshToken) {
      console.error('No access or refresh token found');
      throw new Error('No access or refresh token found');
    }

    // Get SA (other user view) mode
    const previewToken = getPreviewAccessToken();
    if (previewToken) {
      accessToken = previewToken;
    }

    // Set auth header (except for refresh token endpoint)
    if (config.url !== API_ROUTES.REFRESH_TOKEN) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      } as any;
    }

    // Explicitly bust cache for write-heavy endpoints (reordering/undo actions)
    if (['PATCH', 'POST', 'DELETE'].includes(config.method?.toUpperCase() ?? '')) {
      // @ts-ignore
      config.headers = {
        ...config.headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      };
    }

    return new Promise((resolve, reject) => {
      this.enqueue({ config, resolve, reject });
    });
  }

  private onResponse(response: AxiosResponse) {
    this.activeCount--;
    this.processQueue();

    return response;
  }

  private async onError(error: any) {
    this.activeCount--;
    normalizeAxiosError(error);

    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // If this is already a refresh token request that failed, logout everything..
      if (originalRequest.url === API_ROUTES.REFRESH_TOKEN) {
        setTokenLocalStorage('');
        setRefreshTokenLocalStorage('');
        markSessionExpiredForSnackbar();
        window.location.reload();
        // TODO notification.showMessage('reload 2');
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(this.axiosInstance(originalRequest));
          });
        });
      }

      // Start token refresh process
      isRefreshingToken = true;

      try {
        const newAccessToken = await refreshAccessToken();

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Notify all queued requests
        onTokenRefreshed(newAccessToken);

        isRefreshingToken = false;

        // Process the queue again after refresh
        this.processQueue();

        // Retry the original request
        return this.axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshingToken = false;
        refreshSubscribers = [];
        this.processQueue();
        return Promise.reject(normalizeAxiosError(refreshError));
      }
    }

    this.processQueue();
    return Promise.reject(error);
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        return this.onRequest(config);
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const respVal = this.onResponse(response);

        if (respVal?.data) {
          respVal.data = convertIdToString(respVal.data); // Modify response body
        }

        return respVal;
      },
      (error: AxiosError) => {
        console.error('Response error:', error);
        return this.onError(error);
      },
    );
  }
}

const convertIdToString = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item) => convertIdToString(item));
  } else if (typeof data === 'object' && data !== null) {
    return Object.keys(data).reduce((acc, key) => {
      const value = data[key];
      if (key === 'id' && typeof value === 'number') {
        acc[key] = value.toString(); // Convert numeric 'id' to string
      } else if (typeof value === 'object') {
        acc[key] = convertIdToString(value); // Recursively process nested objects
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as any);
  }
  return data;
};

// Create manager and export
export const axiosManager = new AxiosRequestManager(axiosInstance, 6);

// Export the wrapped Axios instance for app-wide use
export default axiosInstance;
